import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { EvidenceNumber } from '../../../types/evidence-numbers'
import { EvidenceStatusBadge } from '../../../components/evidence/EvidenceStatusBadge'
import { EvidenceNumberLink } from '../../../components/evidence/EvidenceNumberLink'
import { EvidenceEmptyState } from './EvidenceEmptyState'

const TH_CLASS =
  'px-4 py-3 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400'

const COLUMN_KEYS = [
  'table.evidenceNumber',
  'table.infrastructure',
  'table.type',
  'table.parent',
  'table.status',
  'table.registered',
] as const

function EvidenceTableHeader(): React.ReactElement {
  const { t } = useTranslation('evidence')
  return (
    <thead className="bg-slate-50 dark:bg-slate-800/50">
      <tr>
        {COLUMN_KEYS.map((key) => (
          <th key={key} className={TH_CLASS}>
            {t(key)}
          </th>
        ))}
      </tr>
    </thead>
  )
}

interface TableRowProps {
  item: EvidenceNumber
  onSelect: (item: EvidenceNumber) => void
}

function EvidenceTableRow({ item, onSelect }: TableRowProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <tr
      className="cursor-pointer border-b border-slate-200 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50"
      onClick={() => {
        onSelect(item)
      }}
    >
      <td className="px-4 py-3">
        <EvidenceNumberLink
          evidenceNumber={item.evidenceNumber}
          numberType={item.numberType}
          onClick={() => {
            onSelect(item)
          }}
        />
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {item.endpointHostname ?? item.endpointName ?? '--'}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {t(`type.${item.numberType}`)}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
        {item.parentEvidenceNumber ?? '--'}
      </td>
      <td className="px-4 py-3">
        <EvidenceStatusBadge status={item.currentStatus} />
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
        {new Date(item.registeredAt).toLocaleDateString()}
      </td>
    </tr>
  )
}

// =============================================================================
// Main View
// =============================================================================

interface EvidenceTableProps {
  data: EvidenceNumber[]
  total: number
  page: number
  perPage: number
  loading: boolean
  /** Current register scope, so an empty result can say *why* it is empty (FR10). */
  activeOnly: boolean
  /** Whether any search/status/type filter is applied (FR10). */
  hasFilters: boolean
  /** Whole-register total, for pointing the user at the wider scope (FR10). */
  registeredTotal: number
  onSelect: (item: EvidenceNumber) => void
  onPageChange: (page: number) => void
}

export function EvidenceTable({
  data,
  total,
  page,
  perPage,
  loading,
  activeOnly,
  hasFilters,
  registeredTotal,
  onSelect,
  onPageChange,
}: EvidenceTableProps): React.ReactElement {

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <RefreshCw className="h-5 w-5 animate-spin text-[var(--accent)]" />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <EvidenceEmptyState
        activeOnly={activeOnly}
        hasFilters={hasFilters}
        registeredTotal={registeredTotal}
      />
    )
  }

  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)
  const totalPages = Math.ceil(total / perPage)

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="w-full text-left">
          <EvidenceTableHeader />
          <tbody>
            {data.map((item) => (
              <EvidenceTableRow key={item.id} item={item} onSelect={onSelect} />
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar
        from={from}
        to={to}
        total={total}
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  )
}

interface PaginationBarProps {
  from: number
  to: number
  total: number
  page: number
  totalPages: number
  onPageChange: (page: number) => void
}

function PaginationBar({
  from,
  to,
  total,
  page,
  totalPages,
  onPageChange,
}: PaginationBarProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <div className="mt-3 flex items-center justify-between text-sm text-[var(--text-muted)]">
      <span>{t('showing', { from, to, total })}</span>
      <div className="flex gap-2">
        <button
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1)
          }}
          className="rounded border border-[var(--border)] px-3 py-1 text-xs disabled:opacity-50"
        >
          &laquo;
        </button>
        <button
          disabled={page >= totalPages}
          onClick={() => {
            onPageChange(page + 1)
          }}
          className="rounded border border-[var(--border)] px-3 py-1 text-xs disabled:opacity-50"
        >
          &raquo;
        </button>
      </div>
    </div>
  )
}
